import React, { useState, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { logger } from '../utils/logger';
import styles from './BrowserLogin.module.css';

interface BrowserLoginProps {
  onLogin: (token: string, user: any) => void;
}

declare global {
  interface Window {
    onTelegramAuth?: (user: any) => void;
  }
}

const BrowserLogin: React.FC<BrowserLoginProps> = ({ onLogin }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const telegramLoginRef = useRef<HTMLDivElement>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [lastError, setLastError] = useState<any>(null);

  // Check for last error on mount
  useEffect(() => {
    const storedError = localStorage.getItem('lastAuthError');
    if (storedError) {
      try {
        const errorData = JSON.parse(storedError);
        setLastError(errorData);
        logger.debug('📋 Last authentication error:', errorData);
      } catch (e) {
        logger.error('Failed to parse stored error:', e);
      }
    }
  }, []);

  const handleTokenLogin = async () => {
    if (!token.trim()) {
      setError('Please enter an auth token');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate token by making a request to the backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      const userData = await response.json();
      onLogin(token, userData);
    } catch (err: any) {
      setError('Invalid token. Please check and try again.');
      logger.error('Token validation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Telegram Login Widget callback
  useEffect(() => {
    // Define the callback function that Telegram will call
    window.onTelegramAuth = async (telegramUser: any) => {
      logger.debug('=== TELEGRAM AUTH CALLBACK START ===');
      logger.debug('Telegram user data:', JSON.stringify(telegramUser, null, 2));
      setTelegramLoading(true);
      setError('');

      try {
        // Create initData in Telegram WebApp format (with 'user' field as JSON)
        const userJson = JSON.stringify({
          id: telegramUser.id,
          first_name: telegramUser.first_name || '',
          last_name: telegramUser.last_name || '',
          username: telegramUser.username || '',
          photo_url: telegramUser.photo_url || ''
        });
        
        const initData = `user=${encodeURIComponent(userJson)}&auth_date=${telegramUser.auth_date}&hash=${telegramUser.hash}`;
        
        logger.debug('📤 Sending to backend (formatted for WebApp):', initData);
        logger.debug('🌐 API URL:', process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
        
        const userData = await authService.authenticate(initData);
        
        logger.debug('✅ Authentication successful!');
        logger.debug('User data:', JSON.stringify(userData, null, 2));
        logger.debug('Token:', userData.token);
        logger.debug('=== TELEGRAM AUTH CALLBACK SUCCESS ===');
        
        onLogin(userData.token, userData.user);
      } catch (err: any) {
        logger.error('=== TELEGRAM AUTH CALLBACK FAILED ===');
        logger.error('Error type:', err.constructor.name);
        logger.error('Error message:', err.message);
        logger.error('Error stack:', err.stack);
        logger.error('Full error object:', JSON.stringify(err, null, 2));
        
        const errorMessage = `Authentication failed: ${err.message || 'Unknown error'}. Check console for details.`;
        setError(errorMessage);
        
        // Store error in localStorage so it persists across refreshes
        localStorage.setItem('lastAuthError', JSON.stringify({
          timestamp: new Date().toISOString(),
          error: err.message,
          stack: err.stack
        }));
      } finally {
        setTelegramLoading(false);
        logger.debug('=== TELEGRAM AUTH CALLBACK END ===');
      }
    };

    // Load Telegram Login Widget script
    if (telegramLoginRef.current && !document.getElementById('telegram-login-script')) {
      const script = document.createElement('script');
      script.id = 'telegram-login-script';
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', 'settlerx_bot');
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-radius', '10');
      script.setAttribute('data-onauth', 'onTelegramAuth(user)');
      script.setAttribute('data-request-access', 'write');
      script.async = true;
      
      telegramLoginRef.current.appendChild(script);
    }

    return () => {
      // Cleanup
      delete window.onTelegramAuth;
    };
  }, [onLogin]);

  const handleGetTokenFromTelegram = () => {
    // Instructions for getting token
    const instructions = `
To get your auth token:

1. Open Settler in Telegram
2. Open browser DevTools (Inspect Element)
3. Go to Console tab
4. Type: localStorage.getItem('authToken')
5. Copy the token (without quotes)
6. Paste it here
    `;
    alert(instructions);
  };

  return (
    <div className={styles.browserLogin}>
      <div className={styles.browserLoginContainer}>
        <div className={styles.browserLoginHeader}>
          <h1>🚀 Settler</h1>
          <p>Browser Debug Mode</p>
        </div>

        <div className={styles.browserLoginContent}>
          <div className={`${styles.loginSection} ${styles.primarySection}`}>
            <h2>🔐 Login with Telegram</h2>
            <p className={styles.loginDescription}>
              Click the button below to authenticate with your Telegram account
            </p>

            {telegramLoading && (
              <div className={styles.loadingMessage}>
                ⏳ Authenticating with Telegram...
              </div>
            )}

            {error && (
              <div className={styles.errorMessage}>
                ❌ {error}
              </div>
            )}

            {lastError && !error && (
              <div className={`${styles.errorMessage} ${styles.persistentError}`}>
                <div className={styles.errorHeader}>
                  ⚠️ Last Authentication Attempt Failed
                  <button 
                    onClick={() => {
                      localStorage.removeItem('lastAuthError');
                      setLastError(null);
                    }}
                    className={styles.clearErrorBtn}
                  >
                    ✕
                  </button>
                </div>
                <div className={styles.errorDetails}>
                  <strong>Time:</strong> {new Date(lastError.timestamp).toLocaleString()}<br/>
                  <strong>Error:</strong> {lastError.error}<br/>
                  <details>
                    <summary>Show stack trace</summary>
                    <pre>{lastError.stack}</pre>
                  </details>
                </div>
              </div>
            )}

            <div ref={telegramLoginRef} className={styles.telegramWidgetContainer}></div>

            <p className={styles.loginNote}>
              ℹ️ You'll be redirected to Telegram to confirm your identity, then back here automatically.
            </p>
          </div>

          <div className={styles.loginDivider}>
            <span>OR</span>
          </div>

          <div className={`${styles.loginSection} ${styles.secondarySection}`}>
            <h2>Advanced: Manual Token Login</h2>
            <p className={styles.loginDescription}>
              Already have an auth token? Paste it here
            </p>

            <details className={styles.advancedLogin}>
              <summary>Show manual token login</summary>
              
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your auth token here..."
                  className={styles.tokenInput}
                  disabled={loading}
                  onKeyPress={(e) => e.key === 'Enter' && handleTokenLogin()}
                />
              </div>

              <button
                onClick={handleTokenLogin}
                disabled={loading || !token.trim()}
                className={`${styles.loginBtn} ${styles.primary} ${styles.small}`}
              >
                {loading ? '⏳ Validating...' : '🔐 Login with Token'}
              </button>

              <button
                onClick={handleGetTokenFromTelegram}
                className={`${styles.loginBtn} ${styles.secondary} ${styles.small}`}
              >
                📱 How to get token?
              </button>
            </details>
          </div>
        </div>

        <div className="browser-login-footer">
          <p>
            <strong>Debug Mode:</strong> This page is for testing and debugging only.
            For production use, please use the Telegram Mini App.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrowserLogin;

